import { useEffect, useState, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { AnimatePresence, motion } from "framer-motion";

interface CountdownComponentProps {
  audioSrc: string;
  isPlaying: boolean;
  hasStartedPlaying: boolean;
  onComplete?: () => void;
  onControlsDisabledChange?: (disabled: boolean) => void;
}

export default function CountdownComponent({
  isPlaying,
  hasStartedPlaying,
  onComplete,
  onControlsDisabledChange,
}: CountdownComponentProps) {
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [sequenceStarted, setSequenceStarted] = useState(false);

  const resetCountdown = useCallback(() => {
    setSequenceStarted(false);
    setCurrentNumber(null);
    setIsComplete(false);
  }, []);

  const startCountdown = useCallback(() => {
    setIsComplete(false);
    let count = 10;

    const countdown = () => {
      if (count >= 1) {
        setCurrentNumber(count);
        count--;
        setTimeout(countdown, 2700);
      } else {
        setCurrentNumber(null);
        setIsComplete(true);
        onControlsDisabledChange?.(false);
        onComplete?.();
      }
    };

    countdown();
  }, [onComplete, onControlsDisabledChange]);

  useEffect(() => {
    if (isPlaying && !sequenceStarted && hasStartedPlaying) {
      setSequenceStarted(true);
      onControlsDisabledChange?.(true);
      startCountdown();
    }

    if (!isPlaying && sequenceStarted) {
      resetCountdown();
      onControlsDisabledChange?.(false);
    }
  }, [
    isPlaying,
    hasStartedPlaying,
    sequenceStarted,
    isComplete,
    onControlsDisabledChange,
    startCountdown,
    resetCountdown,
  ]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <AnimatePresence mode="wait">
          {!hasStartedPlaying && (
            <div className="text-center py-8">
              <p className="text-lg text-white/50">
                <FormattedMessage id="breathing.countdown.pressPlay" />
              </p>
            </div>
          )}

          {hasStartedPlaying && currentNumber && (
            <motion.div
              key={`number-${currentNumber}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              <div className="text-8xl font-bold text-center py-5 text-black">
                {currentNumber}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
